import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';

import { parseServicesFromImageItem } from '../../../../capabilities/image-vm/applyImageServices';
import { normalizeSystemDiskRecord } from '../../../../capabilities/image-vm/utils';
import type { ServiceFormItem } from '../../../../capabilities/service/types';
import { readStoredContainerDeployConfig } from '../../../../platform/container-deploy-config';
import { readCurrentFieldValue, setFieldValueIfChanged } from '../../common/formHelpers';
import { enableSkuModulesForPreset } from '../../common/sku/skuModulePreset';
import { clearImageLinkedResourceComputeFromForm } from './clearImageLinkedResourceCompute';
import { readDiskImageKey } from './diskImageKey';
import { createImageKeySwitchTracker } from './imageSwitchTracker';

const diskImageSwitchTracker = createImageKeySwitchTracker();

export interface ApplyImagePresetServicesOptions {
  /** 换镜像时额外清空的表单 path（如 EBM 的 resourceConfiguration） */
  extraClearOnImageSwitchPaths?: readonly string[];
}

/**
 * 从 optionMeta / system.options.containerDeployConfig 还原镜像项（不再读 systemDisk.services）
 */
function resolveSystemDiskImageItem(
  values: Record<string, unknown>,
  disk: ReturnType<typeof normalizeSystemDiskRecord>,
): CommonImageItemType | null {
  const imageKey = readDiskImageKey(disk);
  if (!imageKey) return null;

  const optionMeta = get(values, 'optionMeta') as Record<string, unknown> | undefined;
  const meta = optionMeta?.[imageKey];
  const storedDeployConfig = readStoredContainerDeployConfig(values);

  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const row = meta as CommonImageItemType;
    return {
      ...row,
      id: String(row.id ?? row.key ?? imageKey),
      key: String(row.key ?? row.id ?? imageKey),
      containerDeployConfig: row.containerDeployConfig ?? storedDeployConfig,
    };
  }

  if (!storedDeployConfig) return null;
  return {
    id: imageKey,
    key: imageKey,
    containerDeployConfig: storedDeployConfig,
  } as CommonImageItemType;
}

/** 解析镜像 apis → 服务表单项 */
function resolvePresetServices(
  values: Record<string, unknown>,
  disk: ReturnType<typeof normalizeSystemDiskRecord>,
): ServiceFormItem[] | undefined {
  return parseServicesFromImageItem(resolveSystemDiskImageItem(values, disk));
}

function writeServices(form: FormInstance, services: ServiceFormItem[] | undefined, servicesPath: string) {
  if (services?.length) {
    setFieldValueIfChanged(form, servicesPath, services);
    enableSkuModulesForPreset(form, ['service']);
  }
}

/** 镜像 key 未变时：仅在表单尚无服务且能从 deployConfig 解析时补写（不回清） */
function syncServicesIfMissing(
  form: FormInstance,
  values: Record<string, unknown>,
  disk: ReturnType<typeof normalizeSystemDiskRecord>,
  servicesPath: string,
) {
  if (!disk) return;
  const services = resolvePresetServices(values, disk);
  if (!services?.length) return;
  const current = readCurrentFieldValue(form, servicesPath);
  if (Array.isArray(current) && current.length > 0) return;
  writeServices(form, services, servicesPath);
}

/** 系统盘镜像变更时：先清空服务配置，再按镜像 containerDeployConfig.apis 预置 */
export function applyImagePresetServicesEffect(
  ctx: { form: FormInstance; values: Record<string, unknown> },
  servicesPath: string,
  options?: ApplyImagePresetServicesOptions,
): void {
  const disk = normalizeSystemDiskRecord(ctx.values.systemDisk);
  const imageKey = readDiskImageKey(disk);
  const imageSwitched = diskImageSwitchTracker.consumeImageSwitch(ctx.form, imageKey);

  if (!imageSwitched) {
    syncServicesIfMissing(ctx.form, ctx.values, disk, servicesPath);
    return;
  }

  if (!disk || !imageKey) {
    setFieldValueIfChanged(ctx.form, servicesPath, undefined);
    clearImageLinkedResourceComputeFromForm(ctx.form, options?.extraClearOnImageSwitchPaths);
    return;
  }

  setFieldValueIfChanged(ctx.form, servicesPath, undefined);
  clearImageLinkedResourceComputeFromForm(ctx.form, options?.extraClearOnImageSwitchPaths);
  writeServices(ctx.form, resolvePresetServices(ctx.values, disk), servicesPath);
}
